import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  Card,
  CardBody,
  Spinner,
  Alert,
  FormFeedback,
  Table,
} from "reactstrap";
import ComponentCard from "./ComponentCard";
import {
  assignCreditsAPI,
  listCreditsByOrgAPI,
  updateUserCreditsAPI,
  bulkUpdateUserCreditsAPI,
  getOrganizationsAPI,
  getUsersByOrgAPI,
} from "../../service/ManageUserCreditsService";
import "../../App.css";

const authService = require("../../service/AuthService");

const ORG_USER_PATTERN = /^[A-Za-z0-9._-]{3,64}$/;
const ADMIN_ORG = "techcomb";

// ---------- helpers for date formatting (UTC) ----------
const pad2 = (n) => String(n).padStart(2, "0");

/** Show as MM-DD-YYYY HH:MM (UTC). Accepts ISO or yyyy-mm-dd */
function formatUtc(s) {
  if (!s) return "";
  const iso = s.includes("T") ? s : `${s}T00:00:00Z`;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return s;
  const mm = pad2(d.getUTCMonth() + 1);
  const dd = pad2(d.getUTCDate());
  const yyyy = d.getUTCFullYear();
  const hh = pad2(d.getUTCHours());
  const min = pad2(d.getUTCMinutes());
  return `${mm}-${dd}-${yyyy} ${hh}:${min}`;
}

const todayISO = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  return `${yyyy}-${mm}-${dd}`;
};

export default function ManageUserCredits() {
  const sessionUser = authService.getUser?.() || {};
  const defaultOrg = (sessionUser?.organization || "").trim().toLowerCase();
  const loggedInUserId = (sessionUser?.user_name || sessionUser?.username || "").trim();
  const loggedInOrg = defaultOrg;

  const [organization, setOrganization] = useState(defaultOrg);
  const [applyAll, setApplyAll] = useState(false);
  const [username, setUsername] = useState("");
  const [credits, setCredits] = useState("");
  const [startDate, setStartDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [orgErr, setOrgErr] = useState("");
  const [userErr, setUserErr] = useState("");
  const [creditsErr, setCreditsErr] = useState("");
  const [dateErr, setDateErr] = useState("");
  const [lastUser, setLastUser] = useState("");
  const [searchOrg, setSearchOrg] = useState(defaultOrg);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchStatus, setSearchStatus] = useState({ type: "", msg: "" });
  const [rows, setRows] = useState([]);
  const [originalRows, setOriginalRows] = useState([]);
  const [rowEditing, setRowEditing] = useState({});
  const [tableEditing, setTableEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orgOptions, setOrgOptions] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersCache, setUsersCache] = useState({}); // { [org]: ["org1","org2"] }

  const trim = (s) => (s ?? "").trim();
  const rowKey = (r) => `${r.org_name}#${r.user_name}`;
  const today = useMemo(() => todayISO(), []); 

  useEffect(() => {
    (async () => {
      try {
        setOrgLoading(true);
        const data = await getOrganizationsAPI({ limit: 500 });
        const items = Array.isArray(data?.items) ? data.items : [];
        setOrgOptions(items);

        // If your org field is currently defaultOrg, ensure it exists in options
        // If not, keep it but dropdown will show it as selected if present.
      } catch (e) {
        setStatus({ type: "danger", msg: e?.message || "Failed to load organizations." });
      } finally {
        setOrgLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const org = trim(organization).toLowerCase();
    if (!org) {
      setUserOptions([]);
      setUsername("");
      return;
    }
    if (applyAll) {
      setUserOptions([]);
      setUsername("");
      return;
    }

    // cached?
    if (usersCache[org]) {
      setUserOptions(usersCache[org]);
      return;
    }

    (async () => {
      try {
        setUsersLoading(true);
        const data = await getUsersByOrgAPI(org, { limit: 2000 });
        const items = Array.isArray(data?.items) ? data.items : [];
        setUserOptions(items);
        setUsersCache((prev) => ({ ...prev, [org]: items }));
      } catch (e) {
        setUserOptions([]);
        setStatus({ type: "danger", msg: e?.message || "Failed to load users for org." });
      } finally {
        setUsersLoading(false);
      }
    })();
  }, [organization, applyAll]);


  const preventInvalidNumberKeys = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
  };

  const validateOrg = (val) => {
    const v = trim(val);
    if (!v) return "Organization is required.";
    if (!ORG_USER_PATTERN.test(v))
      return "3–64 chars; letters, digits, dot, underscore, hyphen only.";
    return "";
  };

  const validateUser = (val, all) => {
    if (all) return "";
    const v = trim(val);
    if (!v) return "User Name is required.";
    if (!ORG_USER_PATTERN.test(v))
      return "3–64 chars; letters, digits, dot, underscore, hyphen only.";
    return "";
  };

  const validateCredits = (val) => {
    const n = Number(val);
    if (!val) return "Credits are required.";
    if (!Number.isInteger(n) || n <= 0) return "Enter a positive whole number.";
    if (n > 100000000) return "Max allowed is 100,000,000.";
    return "";
  };

  const validateDates = (start, end) => {
    if (!start) return "Start Date is required.";
    if (!end) return "Expiry Date is required.";

    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    const todayObj = new Date(today);
    // normalize times
    todayObj.setHours(0, 0, 0, 0);

    if (startDateObj < todayObj) {
      return "Start Date cannot be before today.";
    }
    if (startDateObj > endDateObj) {
      return "Start Date cannot be after Expiry Date.";
    }
    return "";
  };

  const formValid = useMemo(() => {
    const o = !validateOrg(organization);
    const c = !validateCredits(credits);
    const d = !validateDates(startDate, expiryDate);
    const u = applyAll ? true : !validateUser(username, false);
    return o && c && d && u;
  }, [organization, credits, startDate, expiryDate, username, applyAll]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", msg: "" });

    if (!loggedInUserId || !loggedInOrg) {
      setStatus({ type: "danger", msg: "You must be logged in to assign credits." });
      return;
    }
    if (loggedInOrg !== ADMIN_ORG) {
      setStatus({ type: "danger", msg: "Your organization is not authorized to assign credits." });
      return;
    }

    const o = validateOrg(organization);
    const u = validateUser(username, applyAll);
    const c = validateCredits(credits);
    const d = validateDates(startDate, expiryDate);
    setOrgErr(o);
    setUserErr(u);
    setCreditsErr(c);
    setDateErr(d);

    if (o || u || c || d) return;

    try {
      setSubmitting(true);

      const payload = {
        action: "assignCredits",
        scope: applyAll ? "org" : "user",
        organization: trim(organization).toLowerCase(),
        username: applyAll ? undefined : trim(username),
        credits: Number(credits),
        startDate,
        expiryDate,
        updatedBy: loggedInUserId,
      };

      const data = await assignCreditsAPI(payload);
      setStatus({
        type: "success",
        msg: data?.message || "Credits assigned successfully.",
      });
    } catch (err) {
      setStatus({
        type: "danger",
        msg:
          err?.response?.data?.message ||
          err?.message ||
          "Something went wrong while assigning credits.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ======== Search & Table handling ========
  const fetchOrg = async (org) => {
    setSearchStatus({ type: "", msg: "" });
    try {
      setSearchLoading(true);
      const data = await listCreditsByOrgAPI(trim(org).toLowerCase());
      const items = Array.isArray(data?.items) ? data.items : [];
      const normalized = items.map((it) => ({
        org_name: it.org_name,
        user_name: it.user_name,
        available_credits: Number(it.available_credits || 0),
        credits_validity_start_date: it.credits_validity_start_date || "",
        credits_expiry_date: it.credits_expiry_date || "",
        creation_datetime: it.creation_datetime || "",
        last_update_datetime: it.last_update_datetime || "",
        updated_by: it.updated_by || "",
      }));
      setRows(normalized);
      setOriginalRows(JSON.parse(JSON.stringify(normalized)));
      setRowEditing({});
      setTableEditing(false);
      if (!normalized.length) {
        setSearchStatus({ type: "danger", msg: "No users found for this organization." });
      }
    } catch (err) {
      setRows([]);
      setSearchStatus({
        type: "danger",
        msg:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch organization credits.",
      });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    const vErr = validateOrg(searchOrg);
    if (vErr) {
      setSearchStatus({ type: "danger", msg: vErr });
      setRows([]);
      return;
    }
    await fetchOrg(searchOrg);
  };

  // ======= Editing helpers =======
  const enableRowEdit = (rk, on) => {
    setRowEditing((prev) => ({ ...prev, [rk]: on }));
  };

  const enableTableEdit = (on) => {
    setTableEditing(on);
    if (on) {
      const all = {};
      rows.forEach((r) => (all[rowKey(r)] = true));
      setRowEditing(all);
    } else {
      setRowEditing({});
      setRows(JSON.parse(JSON.stringify(originalRows)));
    }
  };

  const handleCellChange = (idx, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      const r = { ...next[idx] };
      if (field === "available_credits") {
        const n = Number(value);
        r.available_credits =
          Number.isFinite(n) && n >= 0 ? Math.floor(n) : r.available_credits;
      } else if (field === "credits_validity_start_date") {
        r.credits_validity_start_date = value;
      } else if (field === "credits_expiry_date") {
        r.credits_expiry_date = value;
      }
      next[idx] = r;
      return next;
    });
  };

  const validateRow = (r) => {
    if (!Number.isInteger(r.available_credits) || r.available_credits < 0) {
      return "Available credits must be a non-negative whole number.";
    }
    if (!r.credits_validity_start_date) return "Start date is required.";
    if (!r.credits_expiry_date) return "Expiry date is required.";

    const startDateObj = new Date(r.credits_validity_start_date);
    const endDateObj = new Date(r.credits_expiry_date);
    const todayObj = new Date(today);
    todayObj.setHours(0, 0, 0, 0);

    if (startDateObj < todayObj) {
      return "Start date cannot be before today.";
    }
    if (startDateObj > endDateObj) {
      return "Start date cannot be after expiry date.";
    }

    return "";
  };


  // ======= Row Submit / Cancel =======
  const handleRowCancel = (rk) => {
    const idx = rows.findIndex((r) => rowKey(r) === rk);
    const orig = originalRows.find((r) => rowKey(r) === rk);
    if (idx >= 0 && orig) {
      setRows((prev) => {
        const next = [...prev];
        next[idx] = { ...orig };
        return next;
      });
    }
    enableRowEdit(rk, false);
  };

  // ======= Row Submit / Cancel =======
  const handleRowSubmit = async (rk) => {
    const r = rows.find((x) => rowKey(x) === rk);
    if (!r) return;

    // Find original row to compare credits
    const orig = originalRows.find((x) => rowKey(x) === rk);
    if (orig && r.available_credits < orig.available_credits) {
      setSearchStatus({
        type: "danger",
        msg: `Cannot reduce credits for ${r.user_name} from ${orig.available_credits} to ${r.available_credits}. Only same or higher values are allowed.`,
      });
      return;
    }

    const err = validateRow(r);
    if (err) {
      setSearchStatus({ type: "danger", msg: err });
      return;
    }

    try {
      setSaving(true);

      await updateUserCreditsAPI({
        action: "updateUserCredits",
        organization: r.org_name,
        user_name: r.user_name,
        available_credits: Number(r.available_credits),
        startDate: r.credits_validity_start_date,
        expiryDate: r.credits_expiry_date,
        updatedBy: loggedInUserId,
      });

      // update local snapshot for this row
      setOriginalRows((prev) => {
        const copy = [...prev];
        const idx = copy.findIndex((x) => rowKey(x) === rk);
        if (idx >= 0) copy[idx] = { ...r };
        return copy;
      });

      // exit edit mode for this row
      enableRowEdit(rk, false);
      setSearchStatus({ type: "success", msg: `Updated ${r.user_name}.` });
    } catch (e) {
      setSearchStatus({
        type: "danger",
        msg: e?.response?.data?.message || e?.message || "Failed to update row.",
      });
    } finally {
      setSaving(false);
    }
  };


  // ======= Table Submit / Cancel =======
  const handleTableCancel = () => {
    enableTableEdit(false);
    setSearchStatus({ type: "", msg: "" });
  };

  const handleTableSubmit = async () => {
    // collect changed rows (only the editable fields)
    const updates = [];
    for (let i = 0; i < rows.length; i++) {
        const a = rows[i];
        const b = originalRows[i];
        if (!b) continue;

        const changedEditable =
        a.available_credits !== b.available_credits ||
        a.credits_validity_start_date !== b.credits_validity_start_date ||
        a.credits_expiry_date !== b.credits_expiry_date;

        if (changedEditable) {
          // Prevent decreasing credits in table edit
          if (a.available_credits < b.available_credits) {
            setSearchStatus({
              type: "danger",
              msg: `Row ${a.user_name}: cannot reduce credits from ${b.available_credits} to ${a.available_credits}. Only same or higher values are allowed.`,
            });
            return;
          }

          const err = validateRow(a);
          if (err) {
            setSearchStatus({ type: "danger", msg: `Row ${a.user_name}: ${err}` });
            return;
          }

          const payloadRow = {
            user_name: a.user_name,
            startDate: a.credits_validity_start_date,
            expiryDate: a.credits_expiry_date,
          };
          if (a.available_credits !== b.available_credits) {
            payloadRow.available_credits = a.available_credits;
          }
          updates.push(payloadRow);
        }
    }

    if (updates.length === 0) {
        setSearchStatus({ type: "info", msg: "No changes to save." });
        enableTableEdit(false);
        return;
    }

    try {
        setSaving(true);

        const resp = await bulkUpdateUserCreditsAPI({
        action: "bulkUpdateUserCredits",
        organization: trim(searchOrg).toLowerCase(),
        updatedBy: loggedInUserId,
        updates,
        });

        // Prefer authoritative count from backend 'results'
        let successCount = 0;
        let failCount = 0;
        if (Array.isArray(resp?.results)) {
        successCount = resp.results.filter(r => r.ok).length;
        failCount = resp.results.length - successCount;
        } else if (typeof resp?.message === "string") {
        // fallback: parse "Updated X row(s)." from message
        const m = resp.message.match(/Updated\s+(\d+)\s+row/);
        successCount = m ? Number(m[1]) : 0;
        const f = resp.message.match(/(\d+)\s+failed/);
        failCount = f ? Number(f[1]) : 0;
        }

        if (successCount > 0) {
        // re-fetch from DynamoDB to ensure we show server truth
        await fetchOrg(searchOrg);
        setSearchStatus({
            type: failCount > 0 ? "warning" : "success",
            msg:
            failCount > 0
                ? `Updated ${successCount} row(s); ${failCount} failed.`
                : `Updated ${successCount} row(s).`,
        });
        enableTableEdit(false);
        } else {
        setSearchStatus({
            type: "danger",
            msg:
            resp?.message ||
            "Bulk update returned no successful rows. Check row validation and try again.",
        });
        }
    } catch (e) {
        setSearchStatus({
        type: "danger",
        msg: e?.response?.data?.message || e?.message || "Failed to update table.",
        });
    } finally {
        setSaving(false);
    }
    };


  return (
    <div className="page-wrapper overflow-hidden">
      <Row className="justify-content-center muc-container">
        <Col sm="12" md="12" lg="12">
          <ComponentCard>
            <div className="muc-wrap" style={{ maxWidth: "500px" }}>
              <h4 className="muc-title">User Credits Management</h4>

              <div className="muc-subtitle" style={{ marginTop: 40, marginBottom: 30 }}>
                Apply credits for individual user or all users in an organization
              </div>

              {status.msg && (
                <Alert
                  color={status.type}
                  className="muc-alert"
                  isOpen={!!status.msg}
                  toggle={() => setStatus({ type: "", msg: "" })}
                >
                  {status.msg}
                </Alert>
              )}

              <Card className="muc-card">
                <CardBody className="muc-card-body">
                  <Form onSubmit={handleSubmit} noValidate>
                    <div className="muc-field-row">
                      <div className="muc-label-cell">Organization:</div>
                      <div>
                        <Input
                          type="select"
                          className="muc-input"
                          value={organization}
                          onChange={(e) => {
                            const v = e.target.value;
                            setOrganization(v);
                            setSearchOrg(v);           // keep search section aligned with selected org (optional but nice)
                            setUsername("");           // reset user when org changes
                            setUserErr("");
                            if (orgErr) setOrgErr(validateOrg(v));
                          }}
                          onBlur={(e) => setOrgErr(validateOrg(e.target.value))}
                          required
                          invalid={!!orgErr}
                        >
                          <option value="" disabled>
                            {orgLoading ? "Loading organizations..." : "Select organization"}
                          </option>
                          {orgOptions.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </Input>
                        {orgErr && <FormFeedback>{orgErr}</FormFeedback>}
                      </div>
                    </div>

                    <div className="muc-field-row">
                      <div className="muc-label-cell">Apply for all users:</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Input
                          type="checkbox"
                          checked={applyAll}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setApplyAll(checked);
                            if (checked) {
                              setLastUser(username);
                              setUsername("");
                              setUserErr("");
                            } else {
                              const candidate = lastUser || "";
                              setUsername(userOptions.includes(candidate) ? candidate : "");
                            }
                          }}
                        />
                      </div>
                    </div>

                    <div className="muc-field-row">
                      <div className="muc-label-cell">User Name:</div>
                      <div>
                        <Input
                          type="select"
                          className={`muc-input ${applyAll ? "muc-input-disabled" : ""}`}
                          value={applyAll ? "" : username}
                          onChange={(e) => {
                            setUsername(e.target.value);
                            if (userErr) setUserErr(validateUser(e.target.value, applyAll));
                          }}
                          onBlur={(e) => setUserErr(validateUser(e.target.value, applyAll))}
                          required={!applyAll}
                          invalid={!applyAll && !!userErr}
                          disabled={applyAll || usersLoading || !organization}
                        >
                          <option value="" disabled>
                            {applyAll
                              ? "-- applies to all users --"
                              : usersLoading
                              ? "Loading users..."
                              : "Select user"}
                          </option>
                          {userOptions.map((u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          ))}
                        </Input>
                        {!applyAll && userErr && <FormFeedback>{userErr}</FormFeedback>}
                      </div>
                    </div>

                    <div className="muc-field-row">
                      <div className="muc-label-cell">Credits:</div>
                      <div>
                        <Input
                          className="muc-input"
                          type="number"
                          inputMode="numeric"
                          min={1}
                          max={100000000}
                          step={1}
                          value={credits}
                          onKeyDown={preventInvalidNumberKeys}
                          onChange={(e) => {
                            setCredits(e.target.value);
                            if (creditsErr) setCreditsErr(validateCredits(e.target.value));
                          }}
                          onBlur={(e) => setCreditsErr(validateCredits(e.target.value))}
                          placeholder={applyAll ? "Total credits for org" : "Credits for user"}
                          required
                          invalid={!!creditsErr}
                        />
                        {creditsErr && <FormFeedback>{creditsErr}</FormFeedback>}
                      </div>
                    </div>

                    <div className="muc-field-row">
                      <div className="muc-label-cell">Start Date:</div>
                      <div>
                        <Input
                          className="muc-input"
                          type="date"
                          value={startDate}
                          min={today} 
                          onChange={(e) => {
                            setStartDate(e.target.value);
                            if (dateErr) setDateErr(validateDates(e.target.value, expiryDate));
                          }}
                          onBlur={(e) => setDateErr(validateDates(e.target.value, expiryDate))}
                          required
                          invalid={!!dateErr}
                        />
                      </div>
                    </div>

                    <div className="muc-field-row">
                      <div className="muc-label-cell">Expiry Date:</div>
                      <div>
                        <Input
                          className="muc-input"
                          type="date"
                          value={expiryDate}
                          min={startDate || undefined}
                          onChange={(e) => {
                            setExpiryDate(e.target.value);
                            if (dateErr) setDateErr(validateDates(startDate, e.target.value));
                          }}
                          onBlur={(e) => setDateErr(validateDates(startDate, e.target.value))}
                          required
                          invalid={!!dateErr}
                        />
                        {dateErr && <FormFeedback>{dateErr}</FormFeedback>}
                      </div>
                    </div>

                    {formValid && (
                      <div className="muc-submit-bar">
                        <Button
                          className="submit-action-button muc-submit-btn"
                          disabled={submitting}
                          type="submit"
                        >
                          {submitting ? (
                            <>
                              <Spinner size="sm" style={{ marginRight: 8 }} /> Submitting...
                            </>
                          ) : (
                            "Submit"
                          )}
                        </Button>
                      </div>
                    )}
                  </Form>
                </CardBody>
              </Card>
            </div>
          </ComponentCard>
        </Col>
      </Row>

      <Row className="justify-content-center muc-container">
        <Col sm="12" md="12" lg="12" style={{ marginTop: 4 }}>
          <ComponentCard>
            <h4 className="muc-title">View credits by organization</h4>

            {searchStatus.msg && (
              <Alert
                color={searchStatus.type}
                className="muc-alert"
                isOpen={!!searchStatus.msg}
                toggle={() => setSearchStatus({ type: "", msg: "" })}
              >
                {searchStatus.msg}
              </Alert>
            )}

            <div className="muc-wrap" style={{ marginTop: 44 }}>
              <Card className="muc-card">
                <CardBody className="muc-card-body">
                  <Form onSubmit={handleSearch}>
                    <div className="muc-field-row">
                      <div className="muc-label-cell">Organization:</div>
                      <div style={{ display: "flex", gap: 30, width: "90%" }}>
                        <Input
                          type="select"
                          className="muc-input"
                          value={searchOrg}
                          onChange={(e) => setSearchOrg(e.target.value)}
                          style={{ maxWidth: 420 }}
                        >
                          <option value="" disabled>
                            {orgLoading ? "Loading organizations..." : "Select organization"}
                          </option>
                          {orgOptions.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </Input>
                        <Button
                          className="submit-action-button muc-submit-btn"
                          type="submit"
                          disabled={searchLoading}
                          size="sm"
                          maxwidth="50%"
                        >
                          {searchLoading ? (
                            <>
                              <Spinner size="sm" style={{ marginRight: 8 }} /> Searching...
                            </>
                          ) : (
                            "Search"
                          )}
                        </Button>

                        {/* Table-level edit controls */}
                        <div style={{ marginLeft: "auto", display: "flex", gap: 12 }}>
                          {!tableEditing ? (
                            <Button
                              size="sm"
                              color="secondary"
                              disabled={rows.length === 0}
                              onClick={() => enableTableEdit(true)}
                            >
                              Edit Table
                            </Button>
                          ) : (
                            <>
                              <Button
                                className="submit-action-button muc-submit-btn"
                                size="sm"
                                disabled={saving}
                                onClick={handleTableSubmit}
                                title="Submit all changes"
                              >
                                ✓ Submit All
                              </Button>
                              <Button
                                size="sm"
                                color="danger"
                                disabled={saving}
                                onClick={handleTableCancel}
                                title="Cancel all edits"
                              >
                                ✕ Cancel
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </Form>

                  {/* Results table */}
                  <div style={{ marginTop: 40, overflowX: "auto", width: "100%" }}>
                    <Table
                      bordered
                      hover
                      size="md"
                      style={{ width: "100%", minWidth: "1000px", tableLayout: "auto" }}
                    >
                      <thead>
                        <tr>
                          <th style={{ whiteSpace: "nowrap" }}>User</th>
                          <th style={{ whiteSpace: "nowrap" }}>Available Credits</th>
                          <th style={{ whiteSpace: "nowrap" }}>Start Date (UTC)</th>
                          <th style={{ whiteSpace: "nowrap" }}>Expiry Date (UTC)</th>
                          <th style={{ whiteSpace: "nowrap" }}>Created (UTC)</th>
                          <th style={{ whiteSpace: "nowrap" }}>Last Updated (UTC)</th>
                          <th style={{ whiteSpace: "nowrap" }}>Updated By</th>
                          <th style={{ whiteSpace: "nowrap" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 ? (
                          <tr>
                            <td colSpan={8} style={{ textAlign: "center", color: "#777" }}>
                              {searchLoading ? "Loading..." : "No data"}
                            </td>
                          </tr>
                        ) : (
                          rows.map((r, idx) => {
                            const rk = rowKey(r);
                            const isEditing = !!rowEditing[rk];
                            return (
                              <tr key={rk}>
                                <td>{r.user_name}</td>

                                {/* Available credits */}
                                <td style={{ maxWidth: 180 }}>
                                  {isEditing ? (
                                    <Input
                                      type="number"
                                      min={0}
                                      step={1}
                                      value={r.available_credits}
                                      onChange={(e) =>
                                        handleCellChange(idx, "available_credits", e.target.value)
                                      }
                                    />
                                  ) : (
                                    r.available_credits ?? 0
                                  )}
                                </td>

                                {/* Start date */}
                                <td style={{ maxWidth: 220 }}>
                                  {isEditing ? (
                                    <Input
                                      type="date"
                                      value={r.credits_validity_start_date || ""}
                                      min={today} 
                                      onChange={(e) =>
                                        handleCellChange(
                                          idx,
                                          "credits_validity_start_date",
                                          e.target.value
                                        )
                                      }
                                    />
                                  ) : (
                                    formatUtc(r.credits_validity_start_date)
                                  )}
                                </td>

                                {/* Expiry date */}
                                <td style={{ maxWidth: 220 }}>
                                  {isEditing ? (
                                    <Input
                                      type="date"
                                      value={r.credits_expiry_date || ""}
                                      min={r.credits_validity_start_date || today}
                                      onChange={(e) =>
                                        handleCellChange(
                                          idx,
                                          "credits_expiry_date",
                                          e.target.value
                                        )
                                      }
                                    />
                                  ) : (
                                    formatUtc(r.credits_expiry_date)
                                  )}
                                </td>

                                {/* Created / Updated */}
                                <td>{formatUtc(r.creation_datetime)}</td>
                                <td>{formatUtc(r.last_update_datetime)}</td>
                                <td>{r.updated_by || ""}</td>

                                {/* Actions */}
                                <td>
                                  {!isEditing ? (
                                    <Button
                                      size="sm"
                                      color="secondary"
                                      onClick={() => enableRowEdit(rk, true)}
                                      disabled={tableEditing}
                                    >
                                      Edit
                                    </Button>
                                  ) : (
                                    <div style={{ display: "flex", gap: 8 }}>
                                      <Button
                                        className="submit-action-button muc-tick-btn"
                                        size="sm"
                                        disabled={saving}
                                        onClick={() => handleRowSubmit(rk)}
                                        title="Submit row"
                                      >
                                        ✓
                                      </Button>
                                      <Button
                                        size="sm"
                                        color="danger"
                                        disabled={saving}
                                        onClick={() => handleRowCancel(rk)}
                                        title="Cancel row"
                                      >
                                        ✕
                                      </Button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </div>
          </ComponentCard>
        </Col>
      </Row>
    </div>
  );
}
